import { useState, useCallback, useRef, useEffect } from 'react'
import type { Quality } from '../types'

interface DownloadFormProps {
  onSubmit: (url: string, quality: Quality) => void
  isLoading: boolean
}

const VALID_QUALITIES: Quality[] = ['highest', 'medium', 'low']

function isValidQuality(value: string): value is Quality {
  return VALID_QUALITIES.includes(value as Quality)
}

function isValidVideoUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

export function DownloadForm({ onSubmit, isLoading }: DownloadFormProps) {
  const [url, setUrl] = useState('')
  const [quality, setQuality] = useState<Quality>('highest')
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault()
      const trimmedUrl = url.trim()
      if (trimmedUrl && isValidVideoUrl(trimmedUrl)) {
        onSubmit(trimmedUrl, quality)
      }
    },
    [url, quality, onSubmit]
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text').trim()
      if (isValidVideoUrl(pastedText)) {
        e.preventDefault()
        setUrl(pastedText)
        onSubmit(pastedText, quality)
      }
    },
    [quality, onSubmit]
  )

  const handleQualityChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value
      if (isValidQuality(value)) {
        setQuality(value)
      }
    },
    []
  )

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor="tweetUrl"
          className="block text-white/80 text-sm mb-2"
        >
          Video URL
        </label>
        <input
          ref={inputRef}
          type="text"
          id="tweetUrl"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onPaste={handlePaste}
          placeholder="https://twitter.com/..., youtube.com/..., etc."
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          disabled={isLoading}
          className="w-full px-4 py-3.5 text-base bg-white/10 border-2 border-white/20 rounded-xl text-white placeholder-white/40 outline-none transition-colors focus:border-twitter-blue disabled:opacity-50"
        />
      </div>

      <div>
        <label
          htmlFor="quality"
          className="block text-white/80 text-sm mb-2"
        >
          Video Quality
        </label>
        <select
          id="quality"
          value={quality}
          onChange={handleQualityChange}
          disabled={isLoading}
          className="w-full px-4 py-3.5 text-base bg-white/10 border-2 border-white/20 rounded-xl text-white outline-none appearance-none cursor-pointer disabled:opacity-50"
        >
          <option value="highest">Highest</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      <button
        type="submit"
        disabled={isLoading || !url.trim() || !isValidVideoUrl(url.trim())}
        className="w-full py-4 text-[17px] font-semibold bg-twitter-blue text-white rounded-xl transition-all hover:bg-twitter-blue-dark hover:-translate-y-0.5 disabled:bg-twitter-blue/50 disabled:cursor-not-allowed disabled:transform-none"
      >
        Download Video
      </button>
    </form>
  )
}
